import React, { FC, useEffect } from 'react';
import { pageTitles } from '@shared/enums/pageTitles';
import { useTitle } from '@shared/hooks/useTitle';
import { Typography, Link, ListItem, Box, List, Chip, Alert, Stack } from '@mui/material';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';

interface Props {
  errorTextMain?: string;
  errorTextSecondary?: string;
  className?: string;
  component?: boolean;
}
export const ClientsBrandPolicyPage: FC<Props> = () => {
  const setPageTitle = useTitle();

  useEffect(() => {
    setPageTitle(pageTitles.clientsBrandPolicyPage);
  }, []);

  return (
    <Box
      sx={(theme) => ({
        [theme.breakpoints.down('sm')]: {
          background: 'white',
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyItems: 'revert',
          gap: 0,
        },
      })}
    >
      <Box
        sx={(theme) => ({
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '59.5rem',
          background: 'white',
          borderRadius: '8px 8px 0 0',
          gridRow: '1/3',
          gridColumn: '1/-1',
          alignSelf: 'center',
          justifySelf: 'center',
          px: 12,
          [theme.breakpoints.down('md')]: {
            px: 8,
            gridRow: 2,
            mt: 1,
          },
          [theme.breakpoints.down('sm')]: {
            display: 'contents',
            px: 0,
          },
        })}
      >
        <Box component={'section'} pb={3}>
          <Typography variant="h3" mb={1} mt={6}>
            Policy for using Clients and Ex-Clients brand names and logos
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            AgileEngine internal websites, Rocky, Rocky AI
            <br />
            Version 1.1 Dec 1, 2024
          </Typography>
          <List
            sx={{
              listStyle: 'auto',
              pl: 2,
            }}
          >
            <ListItem>
              <Link color="text.primary" href="#s1-list">
                List of Clients and Ex-Clients
              </Link>
            </ListItem>
            <ListItem>
              <Link color="text.primary" href="#s2-legal-risks">
                Legal risk categories clarification
              </Link>
            </ListItem>
            <ListItem>
              <Link color="text.primary" href="#s3-areas-of-usage">
                The areas where brand name/logo can be used
              </Link>
            </ListItem>
            <ListItem>
              <Link color="text.primary" href="#s4-actions">
                Action plan for obtaining permissions
              </Link>
            </ListItem>
            <ListItem>
              <Link color="text.primary" href="#s5-additional-rules">
                Additional rules for use and applications
              </Link>
            </ListItem>
          </List>
          <Typography variant="h3" id="s1-list" mt={4} mb={2}>
            Section 1. List of Clients and Ex-Clients
          </Typography>
          <Typography variant="body1" gutterBottom>
            As our Clients' names are a big selling point, we must use them in our marketing materials and
            communications. Here is the reviewed list of Clients and Ex-Clients:{' '}
            <Link
              href="https://docs.google.com/spreadsheets/d/1kQglfdsBQr9QXMLuBYv3sqF6AIHZlhCr_gYwyBX06Ng/edit#gid=382238386"
              target="_blank"
              rel="noopener"
            >
              Permissions for using brand names/logos (Clients, Ex-Clients)
            </Link>
          </Typography>
          <Typography variant="body1" mb={2}>
            For your purposes, please read the usage policy carefully before taking brand names. The brand name usage
            for Clients (1st tab) and Ex-clients (2nd tab) has three applicable statuses: Use Publicly, Use Unlisted,
            NOT Use. To determine the status of the brand name you intend to use please check Permission Status (column
            H).
          </Typography>
          <Chip label="Use publicly" sx={{ background: '#D9ECC0', color: '#377450', fontWeight: '500', px: '2px' }} />
          <List>
            <ListItem>
              We can use the low-risk names on our public pages like our home page and LinkedIn posts, public case
              studies, etc.
            </ListItem>
          </List>
          <Chip label="Use unlisted" sx={{ background: '#FBE5A8', color: '#453824', fontWeight: '500', px: '2px' }} />
          <List>
            <ListItem>
              We can use the medium risk names in non-searchable documents like landing pages and presentations,
              proposals, internal websites, etc.
            </ListItem>
          </List>
          <Chip label="NOT use" sx={{ background: '#A22116', color: '#F8D1CA', fontWeight: '500', px: '2px' }} />
          <List>
            <ListItem>
              We can NOT use high-risk names in any materials without written permission from a Client, so we refer to
              them as “#1 brokerage in the US” for Charles Schwab, etc.
            </ListItem>
            <ListItem>
              Column M gives the information on which Clients require written permission for their brand name usage.
            </ListItem>
            <ListItem>
              This also applies to internal AE materials (for example, presentations) that AE shows to the client.
            </ListItem>
            <ListItem>
              It is possible to blur direct mentions of the name with the general description of the client’s area to
              show the case.
            </ListItem>
          </List>

          <Typography variant="h3" id="s2-legal-risks" mt={4} mb={2}>
            Section 2. Legal risk categories clarification
          </Typography>
          <Chip label="Low risk" sx={{ background: '#D9ECC0', color: '#377450', fontWeight: '500', px: '2px' }} />
          <List>
            <ListItem>
              There are no legal terms related to logo and brand name usage in the MSA, and/or written permission from
              the Client in place.
            </ListItem>
          </List>
          <Chip label="Medium risk" sx={{ background: '#FBE5A8', color: '#453824', fontWeight: '500', px: '2px' }} />
          <List>
            <ListItem>Required written consent on any naming and logo usage under MSA.</ListItem>
          </List>
          <Chip label="High risk" sx={{ background: '#A22116', color: '#F8D1CA', fontWeight: '500', px: '2px' }} />
          <List>
            <ListItem>
              Require written consent + Unlimited liability for such a breach + immediate termination of Agreement +
              Reported violation by Client.
            </ListItem>
          </List>
          <Alert severity="error" icon={<UnsubscribeIcon fontSize="inherit" />}>
            Any domain from the high-risk clients should NOT be included in warm or cold email lists:
          </Alert>
          <Stack direction="row" gap="24px" mt="16px">
            <Box>
              <Typography variant="subtitle1">Reported violation:</Typography>
              <List>
                <ListItem>Indeed</ListItem>
                <ListItem>Bloomberg</ListItem>
                <ListItem>Estee Lauder</ListItem>
              </List>
            </Box>
            <Box>
              <Typography variant="subtitle1">
                Confidentiality issues + Unlimited liability (Material breach):
              </Typography>
              <List>
                <ListItem>Relativity</ListItem>
                <ListItem>Starz</ListItem>
                <ListItem>TeamViewer</ListItem>
                <ListItem>Daimler</ListItem>
                <ListItem>Charles Schwab</ListItem>
              </List>
            </Box>
          </Stack>

          <Typography variant="h3" id="s3-areas-of-usage" mt={4} mb={2}>
            Section 3. Brand names/logos usage based on the category (Legal + CP analysis)
          </Typography>
          <Typography variant="body1">
            Analyze what you can do with the logo based on the category from table permissions:{' '}
            <Link
              href="https://docs.google.com/spreadsheets/d/1VpxXhIviB9y-cC_XmrhUOppFwUfELeNYFUyHcl7s62Y/edit#gid=0"
              target="_blank"
              rel="noopener"
            >
              Usage Cases - clients logos use
            </Link>
          </Typography>

          <Typography variant="h3" id="s4-actions" mt={4} mb={2}>
            Section 4. Action plan for obtaining permissions
          </Typography>
          <Typography variant="body1" mb={2}>
            In case you need to use a brand name/logo of the (i) Medium risk or (ii) High-risk Risk Category, then,
            before using it, you should get in touch with the client and ask for their respective permission (PDM to
            request Client permission when asked).
          </Typography>
          <Alert severity="error">
            Please remember that, if you use any brand name/logo of (i) Medium or (ii) High risk before the permission
            is obtained, it may lead to the client’s disappointment and, as a worst-case scenario, to termination of the
            MSA and/or lawsuit.
          </Alert>
          <Typography variant="subtitle1" gutterBottom mt={2}>
            1. Request permission from the Client
          </Typography>
          <Typography variant="body1" gutterBottom>
            The text of the letter to the client asking for permission to use its brand name/logo may differ and depend
            on your relationship with the client. As a sample, refer to this{' '}
            <Link
              href="https://docs.google.com/document/d/1EmbT1h94SE4WRsh2SFv8LSAcnsW0x0gkhNVHPNlwOFw/edit"
              target="_blank"
              rel="noopener"
            >
              template
            </Link>{' '}
            to ask the client if we can use their logo. Please put{' '}
            <Link href="mailto:client_community@agileengine.com" target="_blank" rel="noopener">
              client_community@agileengine.com
            </Link>{' '}
            and{' '}
            <Link href="mailto:legal@agileengine.com" target="_blank" rel="noopener">
              legal@agileengine.com
            </Link>{' '}
            in the CC of your request to the respective client.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Please also note that certain clients may ask us to provide them, for their review, copies of any planned
            marketing materials or other documents that refer to the client. In such a case, we should provide the
            client with marketing materials or other documents referring to them.
          </Typography>
          <Typography variant="subtitle1" gutterBottom mt={3}>
            2. Request permission from the AgileEngine CEO
          </Typography>
          <Typography variant="body1" gutterBottom>
            <Link
              href="https://docs.google.com/document/d/1A2Hb6ReXtIWt7TzN7szC_1pL8lbewV28jjOVE0WNlmw/edit"
              target="_blank"
              rel="noopener"
            >
              Client name/logo use - approval request from AE CEO
            </Link>
          </Typography>
          <Typography variant="body1" gutterBottom>
            As a result of your initial request, you may receive one of the following answers from the client to your
            request:
          </Typography>
          <List>
            <ListItem>granted permission for the brand name/logo usage, or</ListItem>
            <ListItem>
              request for copies of any planned marketing materials or other documents that refer to the client, or
              other additional information/documents, or
            </ListItem>
            <ListItem>rejection to grant permission for the brand name/logo usage.</ListItem>
          </List>
          <Typography variant="body1" gutterBottom>
            After the receipt of any of the three answers above, please get in touch with{' '}
            <Link href="mailto:client_community@agileengine.com" target="_blank" rel="noopener">
              client_community@agileengine.com
            </Link>{' '}
            and{' '}
            <Link href="mailto:legal@agileengine.com" target="_blank" rel="noopener">
              legal@agileengine.com
            </Link>{' '}
            and forward them the client’s answer. The information on the respective answer will be put into.
          </Typography>
          <Typography variant="body1" gutterBottom>
            We shall not use the client’s brand name/logo while the respective request is pending – until we receive the
            respective granted permission.
          </Typography>
          <Typography variant="body1" gutterBottom>
            In case the client refuses to provide the requested permission, we shall not use its brand name/logo
            anywhere except for our internal communications between the personnel inside AgileEngine only.
          </Typography>

          <Typography variant="h3" id="s5-additional-rules" mt={4} mb={2}>
            Section 5. Additional rules for use and applications
          </Typography>
          <Typography variant="body1" gutterBottom>
            Please remember, when using logos and names:
          </Typography>
          <List>
            <ListItem>
              Limit your use to what is allowed under this policy and/or the respective client (when applicable).
            </ListItem>
            <ListItem>
              Do not modify names or logos in any way, check twice for spelling and/or use authorized versions when
              applicable.
            </ListItem>
            <ListItem>
              Place logos with enough space and in a way they don’t appear combined or connected with other logos in any
              way. Never use logos/names in a way that appears detrimental for the owner.
            </ListItem>
            <ListItem>
              If logos/names are related to statements, verify that the statements are authorized or use only public
              information.
            </ListItem>
            <ListItem>
              Details of our collaboration (i.e. projects assigned, number of resources assigned, deliverables, budgets,
              etc.) are confidential and shall not be made public or shared for marketing purposes even if the use of
              the logo/name is authorized.
            </ListItem>
            <ListItem>You may use general descriptions instead of logos/names.</ListItem>
          </List>
        </Box>
      </Box>
    </Box>
  );
};
